import { useState, useMemo, useEffect } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

interface EspecialidadItem {
  especialidad: string
  total: number
  porcentaje: number
}

interface ProductoItem {
  nombre: string
  total: number
  total_especialidades: number
  especialidades: EspecialidadItem[]
}

interface Dkt0626Data {
  titulo: string
  subtitulo: string
  total_general: number
  total_productos: number
  total_especialidades: number
  productos: ProductoItem[]
  especialidades_unicas: string[]
}

export function Dkt0626Page() {
  const { data, isLoading, error } = useDashReport<Dkt0626Data>('dkt/ginecologia-0626')
  const [productoIndex, setProductoIndex] = useState(0)
  const [tablaOpen, setTablaOpen] = useState(true)
  const [especialidadesSeleccionadas, setEspecialidadesSeleccionadas] = useState<Set<string>>(new Set())

  const producto = data?.productos[productoIndex] ?? data?.productos[0]

  // Al cambiar de producto, seleccionar todas las especialidades del nuevo producto
  useEffect(() => {
    if (!producto) return
    setEspecialidadesSeleccionadas(new Set(producto.especialidades.map((e) => e.especialidad)))
  }, [productoIndex, producto?.nombre])

  const especialidadesFiltradas = useMemo(() => {
    if (!producto) return []
    if (especialidadesSeleccionadas.size === 0) return producto.especialidades
    return producto.especialidades.filter((e) => especialidadesSeleccionadas.has(e.especialidad))
  }, [producto, especialidadesSeleccionadas])

  const totalFiltrado = useMemo(
    () => especialidadesFiltradas.reduce((sum, e) => sum + e.total, 0),
    [especialidadesFiltradas],
  )

  const todasSeleccionadas =
    producto !== undefined &&
    especialidadesSeleccionadas.size === producto.especialidades.length

  function toggleEspecialidad(nombre: string) {
    setEspecialidadesSeleccionadas((prev) => {
      const next = new Set(prev)
      if (next.has(nombre)) {
        next.delete(nombre)
      } else {
        next.add(nombre)
      }
      return next
    })
  }

  function toggleTodas() {
    if (!producto) return
    if (todasSeleccionadas) {
      setEspecialidadesSeleccionadas(new Set())
    } else {
      setEspecialidadesSeleccionadas(new Set(producto.especialidades.map((e) => e.especialidad)))
    }
  }

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
        </div>
      </div>
    )
  }

  if (error || !data || !producto) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  const hayFiltroActivo = especialidadesSeleccionadas.size < producto.especialidades.length

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <a
              href="https://enterprise.tonalstrategies.com/pricing/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              Calculador
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6 space-y-5">

          {/* Selector de producto */}
          <div>
            <p className="flex items-center text-sm font-semibold text-gray-900 mb-2">
              <svg className="w-5 h-5 text-pink-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              Producto
            </p>
            <div className="flex flex-wrap gap-2">
              {data.productos.map((p, idx) => (
                <button
                  key={p.nombre}
                  onClick={() => setProductoIndex(idx)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 border ${
                    idx === productoIndex
                      ? 'bg-pink-600 text-white border-pink-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-pink-400 hover:text-pink-600'
                  }`}
                >
                  {p.nombre}
                </button>
              ))}
            </div>
          </div>

          {/* Filtro multi-select de especialidades */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="flex items-center text-sm font-semibold text-gray-900">
                <svg className="w-5 h-5 text-pink-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                Especialidades
                {hayFiltroActivo && (
                  <span className="ml-2 text-xs font-normal text-pink-600 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5">
                    {especialidadesSeleccionadas.size} de {producto.especialidades.length}
                  </span>
                )}
              </p>
              <button
                onClick={toggleTodas}
                className="text-xs text-pink-600 hover:text-pink-800 font-medium underline underline-offset-2"
              >
                {todasSeleccionadas ? 'Limpiar selección' : 'Seleccionar todas'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {producto.especialidades.map((esp) => {
                const activa = especialidadesSeleccionadas.has(esp.especialidad)
                return (
                  <button
                    key={esp.especialidad}
                    onClick={() => toggleEspecialidad(esp.especialidad)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors duration-200 border ${
                      activa
                        ? 'bg-pink-50 text-pink-700 border-pink-400 font-medium'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <span className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 border ${
                      activa ? 'bg-pink-600 border-pink-600' : 'border-gray-300'
                    }`}>
                      {activa && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    {esp.especialidad}
                    <span className={`text-xs ${activa ? 'text-pink-500' : 'text-gray-400'}`}>
                      ({esp.total.toLocaleString('es-EC')})
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 lg:p-6 border border-pink-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-pink-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {producto.nombre}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{totalFiltrado.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">
                Médicos potenciales{hayFiltroActivo ? ' (filtrado)' : ''}
              </p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-pink-600">{especialidadesFiltradas.length}</p>
              <p className="text-sm text-gray-600 mt-1">
                Especialidades{hayFiltroActivo ? ' seleccionadas' : ''}
              </p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-rose-600">{data.total_general.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total general ({data.total_productos} productos)</p>
            </div>
          </div>
        </div>

        {/* Tabla de especialidades */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setTablaOpen(!tablaOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-pink-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-pink-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="text-base lg:text-lg">Análisis por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({especialidadesFiltradas.length} especialidad{especialidadesFiltradas.length !== 1 ? 'es' : ''})
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${tablaOpen ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {tablaOpen && (
            <div className="mt-4 overflow-x-auto">
              {especialidadesFiltradas.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">
                  Selecciona al menos una especialidad para ver los datos.
                </p>
              ) : (
                <table className="min-w-full bg-white text-xs lg:text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {especialidadesFiltradas.map((esp) => (
                      <tr key={esp.especialidad} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.especialidad}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.total.toLocaleString('es-EC')}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-pink-600">{esp.porcentaje}%</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-pink-600 h-2.5 rounded-full" style={{ width: `${esp.porcentaje}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                    <tr>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{totalFiltrado.toLocaleString('es-EC')}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-pink-600">
                        {hayFiltroActivo
                          ? `${Math.round((totalFiltrado / producto.total) * 100)}%`
                          : '100%'}
                      </td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3" />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Análisis de Base de Datos</p>
        </div>

      </div>
    </div>
  )
}
